import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { RouteHandle } from '@/shared/config';

export interface OpenTab extends RouteHandle {
  /** Pathname + search, so a tab restores its filters. */
  path: string;
  /** Pathname only; identifies the tab. */
  key: string;
}

interface TabsState {
  tabs: OpenTab[];
  open: (tab: OpenTab) => void;
  /** Removes a tab and returns the path to show next if it was the active one. */
  close: (key: string) => OpenTab | undefined;
  /** Closes every tab except `key`; the active screen keeps its tab since it would reopen anyway. */
  closeOthers: (key: string) => void;
  /** Closes every tab after `key` in the bar. */
  closeToRight: (key: string) => void;
  reset: () => void;
}

/** Open-screens bar ("multi-tabs"), kept for the browser session. */
export const useTabsStore = create<TabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      open: (tab) =>
        set((s) => {
          const idx = s.tabs.findIndex((t) => t.key === tab.key);
          if (idx === -1) return { tabs: [...s.tabs, tab] };
          const tabs = s.tabs.slice();
          tabs[idx] = tab;
          return { tabs };
        }),
      close: (key) => {
        const { tabs } = get();
        const idx = tabs.findIndex((t) => t.key === key);
        if (idx === -1) return undefined;
        const next = tabs.filter((t) => t.key !== key);
        set({ tabs: next });
        return next[Math.min(idx, next.length - 1)] ?? next[next.length - 1];
      },
      closeOthers: (key) => set((s) => ({ tabs: s.tabs.filter((t) => t.key === key) })),
      closeToRight: (key) =>
        set((s) => {
          const idx = s.tabs.findIndex((t) => t.key === key);
          return idx === -1 ? s : { tabs: s.tabs.slice(0, idx + 1) };
        }),
      reset: () => set({ tabs: [] }),
    }),
    { name: 'fair-doctor-tabs', storage: createJSONStorage(() => sessionStorage) },
  ),
);
